// Gemini fallback chain — pure logic (no fetch, no React), unit-tested in
// src/__tests__/geminiFallback.test.ts. The loop itself lives in
// aiService.generateWithGeminiFallback.
//
// Where the upstream status comes from: the backend never forwards Google's HTTP
// status as its own. `/api/ai/stream` flushes SSE headers before calling Gemini, so
// an upstream 503 arrives as HTTP 200 + an SSE event {"error": "Gemini API error: 503 - …"};
// `/api/ai/generate` answers 500 with the same message. The upstream status is
// therefore parsed from the message; the proxy's own HTTP status (400 validation,
// 429 express-rate-limit, 502 empty content, 504 timeout) is used otherwise.
import { GEMINI_FALLBACK_CHAIN } from '../config';

/** Error thrown by aiService for a failed AI request; `status` = proxy HTTP status (if any). */
export class AIRequestError extends Error {
  status: number | null;
  constructor(message: string, status: number | null = null) {
    super(message);
    this.name = 'AIRequestError';
    this.status = status;
  }
}

/** What to do after a failed attempt. */
export type GeminiFailureAction = 'retry' | 'next' | 'stop';

const UPSTREAM_STATUS_RE = /Gemini API error:\s*(\d{3})/;
// Empty answer (whole budget spent on thinking) — the next model may well answer.
const EMPTY_CONTENT_RE = /(Strumień zakończony bez treści|zużył cały limit na rozumowanie)/;

/** Google's HTTP status embedded in the backend error message, e.g. "Gemini API error: 503 - …". */
export const extractUpstreamStatus = (message: string): number | null => {
  const m = UPSTREAM_STATUS_RE.exec(message);
  return m ? Number(m[1]) : null;
};

/**
 * Classifies a failed Gemini attempt:
 *  - 'retry' — upstream 503 (overloaded): retry the same model once, then move on;
 *  - 'next'  — upstream 429 (quota) / 500 / 502 / 504, proxy timeout (504), empty content:
 *              move on to the next model without retrying;
 *  - 'stop'  — everything else, notably 400 (bad request — another model would fail
 *              the same way), 401/403/404 on the selected model, the proxy's own 429
 *              (per-user rate limit) and network errors (backend unreachable).
 *              403/404 on a FALLBACK model moves on to the next one.
 */
export const classifyGeminiFailure = (
  error: unknown,
  { isFallback = false }: { isFallback?: boolean } = {}
): GeminiFailureAction => {
  const message = error instanceof Error ? error.message : String(error ?? '');
  const upstream = extractUpstreamStatus(message);
  if (upstream !== null) {
    if (upstream === 503) return 'retry';
    if ([429, 500, 502, 504].includes(upstream)) return 'next';
    // A FALLBACK model that is forbidden/not found for this key (e.g. 2.5 restricted to
    // already-active projects since 2026-09-18) must not end the chain.
    if (isFallback && (upstream === 403 || upstream === 404)) return 'next';
    return 'stop';
  }
  if (EMPTY_CONTENT_RE.test(message)) return 'next';
  const status = error instanceof AIRequestError ? error.status : null;
  if (status === 502 || status === 504) return 'next';
  return 'stop';
};

/**
 * selected → GEMINI_FALLBACK_CHAIN, deduplicated, keeping only fallback ids present in
 * the catalog (`availableIds`). The selected model is always first (the user chose it).
 * With an empty/unknown catalog only the selected model is used.
 */
export const buildGeminiFallbackChain = (
  selected: string,
  availableIds: readonly string[] = [],
  fallbacks: readonly string[] = GEMINI_FALLBACK_CHAIN,
): string[] => {
  const available = new Set(availableIds);
  const chain = [selected];
  for (const id of fallbacks) {
    if (available.has(id) && !chain.includes(id)) chain.push(id);
  }
  return chain;
};

/** Short Polish reason for the UI notice, e.g. "przeciążony (503)". */
export const describeGeminiFailure = (error: unknown): string => {
  const message = error instanceof Error ? error.message : String(error ?? '');
  const status = extractUpstreamStatus(message) ?? (error instanceof AIRequestError ? error.status : null);
  if (status === 503) return 'przeciążony (503)';
  if (status === 429) return 'limit zapytań wyczerpany (429)';
  if (status === 500) return 'błąd serwera (500)';
  if (status === 504) return 'przekroczony czas (504)';
  if (EMPTY_CONTENT_RE.test(message)) return 'brak treści w odpowiedzi';
  return status ? `błąd ${status}` : 'błąd';
};
